import { Box, Flex, SimpleGrid, VStack } from '@chakra-ui/react';
import { Button } from '@pluxee-design-system/core';
import { CheckboxControl, InputControl, SubmitButton } from '@pluxee-design-system/react-hook-form';
import { useCompanyServices } from 'api/companyApi';
import VideoButton from 'common/buttons/VideoButton';
import { AddressControl, CompanyIdControl } from 'common/forms';
import { SplitScreen } from 'common/layouts';
import { TextTitle } from 'common/typography';
import CampaignLogo from 'components/Affiliation/CampaignLogo';
import { WizardStep } from 'components/WizardForm/types';
import { companyIdAsync, createSchema } from 'config';
import useTranslations from 'i18n';
import { intersection } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import gtm from 'trackers/gtm';
import { PageVideoEnum } from 'types/videos';
import scrollToTop from 'utils/scrollToTop';
import { ValidationError } from 'yup';
import { headerTranslationKeys, PrefillStatusEnum } from '../prefillStatus';
import Stepper from '../Stepper';
import { AffiliationValues, ExtraWizardProps } from '../types';
import { CompanyServiceSelect, getActivityNames, LegalFormSelectControl } from './utils';

export const CompanyInfoStep: WizardStep<ExtraWizardProps, AffiliationValues> = ({
  activeStep,
  campaign,
  config: {
    companyInfo: { form: config },
  },
  nextStepProps: { allowedCompanyServices, prefillAddressIncomplete, prefillDataStatus },
  toPrev: toPrevRaw,
  toStep,
}) => {
  const { data: servicesData } = useCompanyServices();
  const services = servicesData?.entries;
  const { t } = useTranslations();
  const { setValue, watch } = useFormContext<AffiliationValues>();
  const prefillStatus = (prefillDataStatus || PrefillStatusEnum.Skipped) as PrefillStatusEnum;
  const isCompanyInfoPrefilled = prefillStatus === PrefillStatusEnum.Prefilled;
  const valuesCountry = watch('country');
  const isAddressDisabled = valuesCountry === 'be' && !prefillAddressIncomplete;

  // values
  const valuesActivity = watch('activity');
  const valuesActivityNames = watch('activityNames');
  const valuesActivitySecondary = watch('activitySecondary');

  const toPrev = useCallback(() => {
    gtm.trackClickCompanyBack(
      Object.values(valuesActivityNames),
      Object.values(valuesActivitySecondary),
    );
    toPrevRaw();
  }, [toPrevRaw, valuesActivityNames, valuesActivitySecondary]);
  const trackSubmit = useCallback(() => {
    gtm.trackClickCompanyConfirm(
      Object.values(valuesActivityNames),
      Object.values(valuesActivitySecondary),
    );
  }, [valuesActivityNames, valuesActivitySecondary]);

  useEffect(() => {
    setValue('activityNames', getActivityNames(valuesActivity, services, t));
  }, [setValue, services, valuesActivity, t]);

  useEffect(() => {
    setValue('activitySecondaryNames', getActivityNames(valuesActivitySecondary, services, t));
  }, [setValue, services, valuesActivitySecondary, t]);

  return (
    <SplitScreen>
      <Flex gap={10} direction="column">
        <Box>
          {campaign && <CampaignLogo campaign={campaign} />}
          <VideoButton page={PageVideoEnum.AffiliationCompanyInfo} />
          <Stepper activeStep={activeStep} onStepChange={toStep} />
          <TextTitle
            mt={6}
            title={t(
              headerTranslationKeys[prefillStatus].header.key,
              headerTranslationKeys[prefillStatus].header.defaultValue,
            )}
            subtitle={t(
              headerTranslationKeys[prefillStatus].subHeader.key,
              headerTranslationKeys[prefillStatus].subHeader.defaultValue,
            )}
          />
        </Box>
        <VStack align="stretch" spacing={4}>
          {config.commercialName.visible && (
            <InputControl
              type="text"
              mandatory={config.commercialName.mandatory}
              name="commercialName"
              id="CompanyInfoStep_commercialName"
              placeholder={t('company_info.form.commercialName.label', 'Commercial name')}
              title={t('company_info.form.commercialName.label', 'Commercial name')}
            />
          )}
          {(config.companyName.visible || config.legalForm.visible) && (
            <SimpleGrid
              columns={{
                base: 1,
                md: config.companyName.visible && config.legalForm.visible ? 2 : 1,
              }}
              spacing={4}
            >
              {config.companyName.visible && (
                <InputControl
                  type="text"
                  mandatory={config.companyName.mandatory}
                  name="companyName"
                  id="CompanyInfoStep_companyName"
                  placeholder={t('company_info.form.companyName.label', 'Company name')}
                  title={t('company_info.form.companyName.label', 'Company name')}
                  disabled={Boolean(isCompanyInfoPrefilled && watch('companyName'))}
                />
              )}
              {config.legalForm.visible && (
                <LegalFormSelectControl
                  mandatory={config.legalForm.mandatory}
                  name="legalForm"
                  id="CompanyInfoStep_legalForm"
                  placeholder={t('company_info.form.legalForm.label', 'Legal form')}
                  title={t('company_info.form.legalForm.label', 'Legal form')}
                  notFoundText={t('global_texts.labels.noResults', 'No results matching a query')}
                  disabled={Boolean(isCompanyInfoPrefilled && watch('legalForm'))}
                />
              )}
            </SimpleGrid>
          )}
          <AddressControl
            config={config}
            disabled={isAddressDisabled}
            idPrefix="CompanyInfoStep"
            tPrefix="company_info.form"
            withSuggestion={valuesCountry === 'cz'}
          />
          {(config.companyId.visible || config.courtFileNumber.visible) && (
            <SimpleGrid
              columns={{
                base: 1,
                md: config.companyId.visible && config.courtFileNumber.visible ? 2 : 1,
              }}
              spacing={4}
            >
              {config.companyId.visible && (
                <CompanyIdControl
                  mandatory={config.companyId.mandatory}
                  name="companyId"
                  id="CompanyInfoStep_companyId"
                  placeholder={t('company_id.form.companyId.placeholder', '12345678')}
                  title={t('company_info.form.companyId.label', 'Company ID')}
                  disabled={Boolean(isCompanyInfoPrefilled && watch('companyId'))}
                  format={config.companyId.format}
                />
              )}
              {config.courtFileNumber.visible && (
                <InputControl
                  type="text"
                  mandatory={config.courtFileNumber.mandatory}
                  name="courtFileNumber"
                  id="CompanyInfoStep_courtFileNumber"
                  placeholder={t('company_info.form.courtFileNumber.label', 'Court file number')}
                  title={t('company_info.form.courtFileNumber.label', 'Court file number')}
                />
              )}
            </SimpleGrid>
          )}
          {config.vatPayer.visible && (
            <CheckboxControl
              // mandatory={config.vatPayer.mandatory}
              name="vatPayer"
              id="CompanyInfoStep_vatPayer"
            >
              {t('company_info.form.vatPayer.label', 'VAT payer')}
            </CheckboxControl>
          )}
          {config.vatNumber.visible && (
            <InputControl
              type="text"
              mandatory={config.vatNumber.mandatory || watch('vatPayer')}
              name="vatNumber"
              id="CompanyInfoStep_vatNumber"
              placeholder={t('company_info.form.vatNumber.label', 'VAT number')}
              title={t('company_info.form.vatNumber.label', 'VAT number')}
              disabled={!watch('vatPayer')}
            />
          )}
          {config.activity.visible && (
            <CompanyServiceSelect
              variant={config.activity.format !== 'SINGLE' ? 'multiple' : 'single'}
              mandatory={config.activity.mandatory}
              name="activity"
              id="CompanyInfoStep_activity"
              placeholder={t('company_info.sectionTexts.activityHeader', 'Activity')}
              title={t('company_info.sectionTexts.activityHeader', 'Activity')}
              notFoundText={t('global_texts.labels.noResults', 'No results matching a query')}
              onlyAllowed={allowedCompanyServices as string[]}
              sortAlphabetically={valuesCountry !== 'cz'}
            />
          )}
          {config.activityAllowSecondary.visible && allowedCompanyServices?.length > 0 && (
            <>
              <CheckboxControl
                name="activityAllowSecondary"
                id="CompanyInfoStep_activityAllowSecondary"
              >
                {t(
                  'company_info.form.activityAllowSecondary.label',
                  'I need to add additional company service(s)',
                )}
              </CheckboxControl>
              {watch('activityAllowSecondary') && (
                <CompanyServiceSelect
                  variant={config.activity.format !== 'SINGLE' ? 'multiple' : 'single'}
                  name="activitySecondary"
                  id="CompanyInfoStep_activitySecondary"
                  title={t(
                    'company_info.sectionTexts.activitySecondaryHeader',
                    'Please let us know which one is missing',
                  )}
                  notFoundText={t('global_texts.labels.noResults', 'No results matching a query')}
                  omitAllowed={allowedCompanyServices}
                />
              )}
            </>
          )}
          {campaign?.isActive && campaign?.config?.visible && (
            <InputControl
              type="text"
              mandatory={campaign?.config?.mandatory}
              name="campaignMemberId"
              id="CompanyInfoStep_campaignMemberId"
              placeholder={campaign?.config?.placeholder || 'Member ID'}
              title={campaign?.config?.placeholder || 'Member ID'}
            />
          )}
        </VStack>
        <VStack align="stretch" spacing={4}>
          <SubmitButton
            variant="primaryFilled"
            id="CompanyInfoStep__validateButton"
            onClick={trackSubmit}
            width="100%"
          >
            {t('global_texts.buttons.confirm', 'Confirm')}
          </SubmitButton>
          <Button
            variant="primaryOutlined"
            id="CompanyInfoStep__backButton"
            type="button"
            onClick={toPrev}
            width="100%"
            background="semantic.surface.1"
          >
            {t('global_texts.buttons.back', 'Back')}
          </Button>
        </VStack>
      </Flex>
    </SplitScreen>
  );
};

CompanyInfoStep.initial = () => ({
  companyName: '',
  commercialName: '',
  legalForm: '',
  streetName: '',
  box: '',
  houseNumber: '',
  orientationNumber: '',
  activity: [],
  activityAllowSecondary: false,
  activitySecondary: [],
  activityNames: {},
  activitySecondaryNames: {},
  postalCode: '',
  city: '',
  vatPayer: false,
  vatNumber: '',
  companyId: '',
  courtFileNumber: '',
  campaignMemberId: '',
});

CompanyInfoStep.validate =
  ({ campaign, config }) =>
  async (values) => {
    let errors: ValidationError[] = [];

    try {
      let syncSchema = createSchema(config.companyInfo.form);
      if (campaign && campaign?.isActive) {
        syncSchema = syncSchema.concat(createSchema({ campaignMemberId: campaign.config }));
      }
      await syncSchema.validateSync(values, { abortEarly: false });
    } catch (validationErrors) {
      // abortEarly = false, must access inner
      errors = validationErrors.inner;
    }

    return errors;
  };

CompanyInfoStep.validateSubmit = async (values, { callCache, config }) => {
  let errors: ValidationError[] = [];

  try {
    const asyncSchema = companyIdAsync(callCache, config.companyId.form.companyId.visible);
    await asyncSchema.validate(values);
  } catch (error) {
    errors = [error];
  }

  return errors;
};

CompanyInfoStep.onSubmit = async (values, _setNextStepProps, extraProps) => {
  scrollToTop();

  return {
    products: values.products.filter((selectedProduct) => {
      const product = extraProps?.products?.find((p) => p.uid === selectedProduct.productUid);
      return (
        product !== undefined &&
        intersection(values.activity, product.services).length > 0 &&
        (product.payoutList
          ? product.payoutList.some((py) =>
              py.channelList.some((ch) => ch.payoutUid === selectedProduct.payoutUid),
            )
          : true)
      );
    }),
  };
};

export default CompanyInfoStep;
